import { useEffect, useRef, useState } from "react";
import {
  trackFormStart,
  trackFormFieldFocus,
  trackFormFieldBlur,
  trackFormFieldChange,
  trackFormSubmit,
  trackFormError,
  trackFormAbandon,
} from "../utils/analytics";

/**
 * Hook for automatic form tracking
 * Tracks form start, field interactions, submit, errors, and abandonment
 *
 * @param {string} formName - Name/identifier of the form
 * @param {string} formId - Optional form ID
 * @param {Object} options - Configuration options
 * @returns {Object} - Form tracking handlers
 *
 * @example
 * const { handleSubmit, handleFieldFocus, handleFieldBlur, handleFieldChange } = useFormTracking('contact_form', 'contact-form-1');
 */
export const useFormTracking = (formName, formId = null, options = {}) => {
  const {
    trackStart = true,
    trackFieldInteractions = true,
    trackErrors = true,
    trackAbandon = true,
    abandonTimeout = 30000, // 30 seconds
  } = options;

  const [fieldsCompleted, setFieldsCompleted] = useState(0);
  const [fieldValues, setFieldValues] = useState({});
  const abandonTimerRef = useRef(null);
  const formStartTimeRef = useRef(null);
  const hasSubmittedRef = useRef(false);

  // Track form start
  useEffect(() => {
    if (trackStart) {
      formStartTimeRef.current = Date.now();
      trackFormStart(formName, formId);
    }

    // Set up abandon tracking
    if (trackAbandon) {
      abandonTimerRef.current = setTimeout(() => {
        if (!hasSubmittedRef.current) {
          trackFormAbandon(formName, fieldsCompleted);
        }
      }, abandonTimeout);
    }

    return () => {
      if (abandonTimerRef.current) {
        clearTimeout(abandonTimerRef.current);
      }
    };
  }, [formName, formId, trackStart, trackAbandon, abandonTimeout]);

  // Track field focus
  const handleFieldFocus = (fieldName, fieldType = "text") => {
    if (!trackFieldInteractions) return;

    trackFormFieldFocus(formName, fieldName, fieldType);
  };

  // Track field blur
  const handleFieldBlur = (fieldName) => {
    if (!trackFieldInteractions) return;

    const hasValue = Boolean(fieldValues[fieldName]);
    trackFormFieldBlur(formName, fieldName, hasValue);
  };

  // Track field change
  const handleFieldChange = (fieldName, value, fieldType = "text") => {
    if (!trackFieldInteractions) return;

    const valueLength = value ? String(value).length : 0;
    const previousValue = fieldValues[fieldName];
    const wasEmpty = !previousValue || previousValue.length === 0;
    const nowHasValue = value && value.length > 0;

    // Update field values
    setFieldValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Track change
    trackFormFieldChange(formName, fieldName, fieldType, valueLength);

    // Update completed fields count
    if (wasEmpty && nowHasValue) {
      setFieldsCompleted((prev) => prev + 1);
    } else if (!nowHasValue && wasEmpty === false) {
      setFieldsCompleted((prev) => Math.max(0, prev - 1));
    }
  };

  // Track form submit
  const handleSubmit = (success = true) => {
    hasSubmittedRef.current = true;

    if (abandonTimerRef.current) {
      clearTimeout(abandonTimerRef.current);
    }

    trackFormSubmit(formName, formId, success);
  };

  // Track form errors
  const handleError = (fieldName, errorType, errorMessage = null) => {
    if (!trackErrors) return;

    trackFormError(formName, fieldName, errorType, errorMessage);
  };

  // Get form completion percentage
  const getCompletionPercentage = (totalFields) => {
    if (!totalFields) return 0;
    return Math.round((fieldsCompleted / totalFields) * 100);
  };

  return {
    handleFieldFocus,
    handleFieldBlur,
    handleFieldChange,
    handleSubmit,
    handleError,
    fieldsCompleted,
    getCompletionPercentage,
  };
};

